import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from 'utils/base-query'
import { buildQueryString, CHSort } from '../candles.types'
import { PolymarketTradeOutcome, PolymarketTradeSide } from './api'

/**
 * Предторговые данные: что стояло в стакане и по чему торговали ДО открытия
 * окна рынка. Сбор начинается за 30 минут до старта.
 *
 * Единицы: boundary / secondsToOpen / firstSeenSec / lastSeenSec — СЕКУНДЫ,
 * ts — миллисекунды. См. docs/preopen-api.md.
 */

export enum PolymarketPreopenSide {
  /** По чему можно было продать. */
  BID = 'BID',
  /** По чему можно было купить. */
  ASK = 'ASK'
}

export type PolymarketPreopenLevel = {
  series: string
  market: string
  asset: string
  outcome: PolymarketTradeOutcome
  side: PolymarketPreopenSide
  price: number
  /** Максимум, стоявший на уровне за весь предторговый период. */
  maxSize: number
  /** Осталось на последнее наблюдение; 0 — заявку сняли до открытия. */
  lastSize: number
  /** Начало окна рынка, секунды. */
  boundary: number
  /** Секунды ДО открытия, поэтому «раньше» — большее число. */
  firstSeenSec: number
  lastSeenSec: number
}

export type PolymarketPreopenTrade = {
  series: string
  market: string
  asset: string
  outcome: PolymarketTradeOutcome
  side: PolymarketTradeSide
  price: number
  size: number
  boundary: number
  secondsToOpen: number
  /** Миллисекунды, в отличие от остальных полей. */
  ts: number
}

export interface PreopenLevelFilter {
  series?: string
  market?: string
  outcome?: PolymarketTradeOutcome
  side?: PolymarketPreopenSide
  boundary?: number
  startBoundary?: number
  endBoundary?: number
  minPrice?: number
  maxPrice?: number
  minLastSize?: number
  limit?: number
  offset?: number
  sort?: CHSort
}

export interface PreopenTradeFilter {
  series?: string
  market?: string
  outcome?: PolymarketTradeOutcome
  side?: PolymarketTradeSide
  boundary?: number
  startBoundary?: number
  endBoundary?: number
  maxSecondsToOpen?: number
  minSecondsToOpen?: number
  limit?: number
  offset?: number
  sort?: CHSort
}

/** limit на бэке по умолчанию 10 (максимум 5000) — задаём всегда явно. */
const DEFAULT_LIMIT = 500

export const apiPreopen = createApi({
  reducerPath: 'apiPreopen',
  baseQuery,
  // Предторги прошедшего окна уже не меняются — в отличие от coins/last-trades,
  // где стоит keepUnusedDataFor: 0. Актуальность живого окна обеспечивает
  // pollingInterval на стороне компонента.
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getPreopenLevels: builder.query<PolymarketPreopenLevel[], PreopenLevelFilter>({
      query: (params) => `polymarket/preopen/levels?${ buildQueryString({limit: DEFAULT_LIMIT, ...params}) }`
    }),
    getPreopenTrades: builder.query<PolymarketPreopenTrade[], PreopenTradeFilter>({
      query: (params) => `polymarket/preopen/trades?${ buildQueryString({limit: DEFAULT_LIMIT, ...params}) }`
    })
  })
})

export const {
  useGetPreopenLevelsQuery,
  useGetPreopenTradesQuery
} = apiPreopen
